import { useEffect, useMemo, useState, type ComponentType, type ReactElement } from "react";
import { NavLink, Outlet, matchPath, useLocation } from "react-router-dom";
import * as Tooltip from "@radix-ui/react-tooltip";
import { ChevronRight, LogOut, Menu, PanelLeftClose, PanelLeftOpen, X } from "lucide-react";
import { PreviewSpacer } from "./ShellParts";
import { HeaderSlotContext } from "./headerSlots";
import { LogoWatermark, Wordmark } from "./Chrome";
import { DemoBadge, cn } from "./ui";
import { useAuth } from "../context/AuthContext";

export interface NavItem {
  to: string;
  label: string;
  slug: string;
  icon: ComponentType<{ className?: string }>;
  end?: boolean;
  section?: string;
}

export interface NavSection { key: string; label: string }

interface ShellProps {
  nav: NavItem[];
  sections?: NavSection[];
  testId: string;
  homePath: string;
  userName: string;
  userEmail: string;
  isDemo: boolean;
  roleLabel: string;
}

const COLLAPSE_KEY = "gdt.sidebar.collapsed";

function Tip({ label, show, children, wrap = false }: { label: string; show: boolean; children: ReactElement; wrap?: boolean }) {
  if (!show) return children;
  return (
    <Tooltip.Root>
      <Tooltip.Trigger asChild>{wrap ? <span className="block">{children}</span> : children}</Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Content side="right" sideOffset={10} className="glass-overlay z-[60] rounded-[10px] px-3 py-1.5 text-xs font-medium text-white shadow-lg">{label}</Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  );
}

function NavLinks({ nav, onNavigate, collapsed = false }: { nav: NavItem[]; onNavigate?: () => void; collapsed?: boolean }) {
  return (
    <>
      {nav.map(({ to, label, slug, icon: Icon, end }) => (
        <Tip key={to} label={label} show={collapsed} wrap>
        <NavLink
          to={to}
          aria-label={collapsed ? label : undefined}
          end={end}
          onClick={onNavigate}
          data-testid={`nav-${slug}`}
          className={({ isActive }) =>
            cn(
              "relative mx-3 flex items-center gap-3 rounded-[12px] px-3 py-2.5 text-sm transition-[background-color,color,box-shadow] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/70",
              collapsed && "justify-center px-0",
              "before:absolute before:left-0 before:top-1/2 before:h-5 before:w-[3px] before:-translate-y-1/2 before:rounded-full before:transition-opacity",
              isActive
                ? "bg-[rgba(255,29,0,0.10)] text-white shadow-[inset_0_0_0_1px_rgba(255,29,0,0.28),0_0_20px_-8px_rgba(255,29,0,0.7)] before:bg-signal before:opacity-100 before:shadow-[0_0_10px_rgba(255,29,0,0.9)]"
                : "text-zinc-400 before:opacity-0 hover:bg-white/[0.06] hover:text-white",
            )
          }
        >
          <Icon className="h-4 w-4 shrink-0 stroke-[1.6]" />
          <span className={collapsed ? "sr-only" : undefined}>{label}</span>
        </NavLink>
        </Tip>
      ))}
    </>
  );
}

const SECTIONS_KEY = "gdt.sidebar.sections";
const readOpen = (): Record<string, boolean> => {
  try { return JSON.parse(localStorage.getItem(SECTIONS_KEY) ?? "{}") ?? {}; } catch { return {}; }
};

function useActiveSection(nav: NavItem[]): string | undefined {
  const { pathname } = useLocation();
  const hit = [...nav].sort((a, b) => b.to.length - a.to.length)
    .find((n) => matchPath({ path: n.to, end: !!n.end }, pathname));
  return hit?.section;
}

function Accordion({ nav, sections, onNavigate, idPrefix }: { nav: NavItem[]; sections: NavSection[]; onNavigate?: () => void; idPrefix: string }) {
  const active = useActiveSection(nav);
  const [open, setOpen] = useState<Record<string, boolean>>(readOpen);
  useEffect(() => { if (active) setOpen((o) => (o[active] ? o : { ...o, [active]: true })); }, [active]);
  const toggle = (key: string) => setOpen((o) => {
    const next = { ...o, [key]: key === active ? true : !o[key] };
    try { localStorage.setItem(SECTIONS_KEY, JSON.stringify(next)); } catch { /* storage off */ }
    return next;
  });
  return (
    <>
      {sections.map(({ key, label }) => {
        const expanded = key === active || !!open[key];
        const id = `${idPrefix}-section-${key}`;
        return (
          <div key={key} className="mb-1">
            <button type="button" onClick={() => toggle(key)} aria-expanded={expanded} aria-controls={id}
              data-testid={`nav-section-${key}`} data-expanded={expanded ? "true" : "false"}
              className="mx-3 mt-2 flex w-[calc(100%-1.5rem)] items-center justify-between rounded-[8px] px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-zinc-400 transition-colors hover:text-zinc-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/70">
              {label}
              <ChevronRight aria-hidden className={cn("h-3.5 w-3.5 transition-transform duration-200 motion-reduce:transition-none", expanded && "rotate-90")} />
            </button>
            <div id={id} className={cn("grid transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none", expanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]")}>
              <div className="flex min-h-0 flex-col gap-0.5 overflow-hidden" inert={!expanded}>
                <NavLinks nav={nav.filter((n) => n.section === key)} onNavigate={onNavigate} />
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
}

function NavList({ nav, sections, onNavigate, collapsed = false, idPrefix = "side" }: { nav: NavItem[]; sections?: NavSection[]; onNavigate?: () => void; collapsed?: boolean; idPrefix?: string }) {
  if (sections && !collapsed) {
    return <nav className="flex flex-col" data-testid="shell-nav"><Accordion nav={nav} sections={sections} onNavigate={onNavigate} idPrefix={idPrefix} /></nav>;
  }
  return (
    <nav className="flex flex-col gap-0.5" data-testid="shell-nav">
      {sections ? sections.map(({ key }, i) => (
        <div key={key} className={cn("flex flex-col gap-0.5", i > 0 && "mt-1.5 border-t border-white/10 pt-1.5")} data-testid={`nav-rail-group-${key}`}>
          <NavLinks nav={nav.filter((n) => n.section === key)} onNavigate={onNavigate} collapsed={collapsed} />
        </div>
      )) : <NavLinks nav={nav} onNavigate={onNavigate} collapsed={collapsed} />}
    </nav>
  );
}

export function AppShell({ nav, sections, testId, homePath, userName, userEmail, isDemo, roleLabel }: ShellProps) {
  const { logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [titleEl, setTitleEl] = useState<HTMLDivElement | null>(null);
  const [actionsEl, setActionsEl] = useState<HTMLDivElement | null>(null);
  const [mobileEl, setMobileEl] = useState<HTMLDivElement | null>(null);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSE_KEY) === "1");
  const toggle = () => setCollapsed((c) => { localStorage.setItem(COLLAPSE_KEY, c ? "0" : "1"); return !c; });
  const toggleBtn = (
    <Tip label={collapsed ? "Expand sidebar" : "Collapse sidebar"} show>
      <button type="button" onClick={toggle} data-testid="sidebar-collapse-toggle" aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        aria-expanded={!collapsed} aria-controls="app-sidebar"
        className="inline-flex items-center gap-1.5 rounded-[10px] border border-white/10 bg-white/[0.04] p-2 text-xs font-medium text-zinc-300 transition-colors hover:border-white/25 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/70">
        {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <><PanelLeftClose className="h-4 w-4" /><span aria-hidden>Collapse</span></>}
      </button>
    </Tip>
  );
  const initial = (userName.trim()[0] ?? "?").toUpperCase();
  const slots = useMemo(() => ({ title: titleEl, actions: actionsEl, mobileActions: mobileEl }), [titleEl, actionsEl, mobileEl]);
  const railFooter = (
    <div className="m-3 flex flex-col items-center gap-2 rounded-[16px] border border-white/10 bg-white/[0.03] py-3">
      {toggleBtn}
      <Tip label={userName} show>
        <span tabIndex={0} role="img" aria-label={userName} data-testid="sidebar-user-avatar"
          className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 bg-white/[0.06] text-sm font-semibold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/70">
          {initial}
        </span>
      </Tip>
      <p data-testid="shell-user-name" className="sr-only">{userName}</p>
      <Tip label={`${userName} · Sign out`} show>
        <button type="button" onClick={() => void logout()} data-testid="logout-button" aria-label="Sign out"
          className="rounded-[10px] p-2 text-zinc-300 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/70">
          <LogOut className="h-4 w-4" />
        </button>
      </Tip>
    </div>
  );

  const renderFooter = (toggle?: ReactElement) => (
    <div className="m-3 rounded-[16px] border border-white/10 bg-white/[0.03] p-4">
      <div className="flex items-center gap-2">
        <p data-testid="shell-user-name" className="truncate text-sm font-medium text-white">{userName}</p>
        <DemoBadge show={isDemo} testId="shell-user-demo-badge" />
      </div>
      <p className="truncate text-xs text-zinc-500">{userEmail}</p>
      <p className="label-caps sidebar-role mt-2">{roleLabel}</p>
      <div className="mt-4 flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => void logout()}
          data-testid="logout-button"
          className="inline-flex items-center gap-2 rounded-full px-1 text-xs font-medium text-zinc-300 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/70"
        >
          <LogOut className="h-4 w-4" /> Sign out
        </button>
        {toggle}
      </div>
    </div>
  );

  return (
    <Tooltip.Provider delayDuration={150}>
    <div data-testid={testId} className="flex min-h-[calc(100vh-var(--banner-h))]">
      <aside id="app-sidebar" data-testid="sidebar" data-collapsed={collapsed ? "true" : "false"}
        className={cn("glass sticky top-[var(--banner-h)] hidden h-[calc(100vh-var(--banner-h))] shrink-0 flex-col overflow-hidden rounded-none border-y-0 border-l-0 transition-[width] duration-300 ease-out motion-reduce:transition-none lg:flex",
          collapsed ? "w-[76px]" : "w-64")}>
        <LogoWatermark rail={collapsed} />
        <div className="relative z-10 flex-1 overflow-y-auto overflow-x-hidden pb-2 pt-4"><NavList nav={nav} sections={sections} collapsed={collapsed} /></div>
        <div className="relative z-10">{collapsed ? railFooter : renderFooter(toggleBtn)}</div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header data-testid="shell-header" className="glass sticky top-[var(--banner-h)] z-30 flex min-h-14 items-center gap-3 rounded-none border-x-0 border-t-0 px-4 py-2 lg:min-h-16 lg:gap-6 lg:px-14">
          <span className="shrink-0 lg:hidden"><Wordmark to={homePath} variant="mark" /></span>
          <div ref={setTitleEl} data-testid="shell-header-title" className="min-w-0 flex-1" />
          <div ref={setActionsEl} data-testid="shell-header-actions" className="hidden min-w-0 max-w-[70%] justify-end py-1 lg:flex" />
          <button type="button" onClick={() => setOpen(true)} data-testid="mobile-menu-button" aria-label="Open menu"
            className="shrink-0 rounded-[12px] border border-white/15 bg-white/[0.05] p-2 text-white transition-colors hover:border-white/30 lg:hidden">
            <Menu className="h-5 w-5" />
          </button>
        </header>
        <main className="flex-1 px-5 py-6 sm:px-10 lg:px-14 lg:py-10">
          <div ref={setMobileEl} data-testid="shell-mobile-actions" className="mb-6 empty:hidden lg:hidden" />
          <HeaderSlotContext.Provider value={slots}>
            <Outlet />
          </HeaderSlotContext.Provider>
        </main>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="mobile-menu">
          <div className="absolute inset-0 animate-fade bg-black/60 backdrop-blur-[3px]" onClick={() => setOpen(false)} />
          <div className="glass-overlay absolute inset-y-0 left-0 flex w-72 animate-slide-in flex-col rounded-r-[22px] border-l-0">
            <LogoWatermark />
            <PreviewSpacer />
            <div className="relative z-10 flex items-center justify-end px-4 pb-1 pt-4">
              <button type="button" onClick={() => setOpen(false)} data-testid="mobile-menu-close-button" aria-label="Close menu" className="p-2 text-zinc-400 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="relative z-10 flex-1 overflow-y-auto"><NavList nav={nav} sections={sections} idPrefix="drawer" onNavigate={() => setOpen(false)} /></div>
            <div className="relative z-10">{renderFooter()}</div>
          </div>
        </div>
      )}
    </div>
    </Tooltip.Provider>
  );
}
